import React, { useState, useMemo } from 'react';
import { View, LogBox, Dimensions, Modal, ScrollView } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { Loader } from '../../Common/Loader';
import ToastMessage from '../common/controls/Toast';
import {
  filterData,
  handleFilterReset,
  handleExportClick,
  constructData,
  EnableExport,
  CommonStyles,
  GetConsolidatedLog,
  FilterChoices
} from './ConsolidatedController';
import { FilterIcon, ExportIcon, FilterBox, SelectedFilters } from './ReUsableComps';
import formCustomStyles from '../common/forms/formStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType3, colorType5, logStyles } from '../LogControllers/LogStyles';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import processData from '../common/service/dataProcessor';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

import { useQuery } from '@tanstack/react-query';

export default function ConsolSpoilLog(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  if (isLoading) return <Loader />;

  return reportData && reportData.items && reportData.columns ? (
    <CreateLogCharts
      spoilLogData={reportData.items}
      spoilLogColumns={reportData.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      clientAPIURL={clientAPIURL}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateLogChartsProps {
  spoilLogData: any;
  spoilLogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateLogCharts: React.FC<CreateLogChartsProps> = ({ spoilLogData, spoilLogColumns, siteId, logTitle, clientAPIURL, isDashboard, packageFilter }) => {
  const [FilterVisible, setFilterVisible] = React.useState(false);
  const screenWidth = Dimensions.get('window').width;
  const { getAccessToken } = useAuth();

  const ColumnsWithIDColumn = spoilLogColumns && [
    ...spoilLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const updatedData = useMemo(() => {
    const raw = processData(logTitle, spoilLogData);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, spoilLogData, packageFilter]);

  const [selectedYear, setSelectedYear] = useState<string>('ALL');
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [selectedPackage, setSelectedPackage] = useState<string>('ALL');
  const [toastVisibility, setToastVisibility] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>('');
  const [newData, setNewData] = useState<any[]>(updatedData);

  // dateRangeFiltered: LogDateRangeFilter applied to newData (year/month/package filtered)
  const [dateRangeFiltered, setDateRangeFiltered] = useState<any[]>(updatedData);

  function returnDate(value: Date) {
    if (value) {
      const date = new Date(value);
      const timezoneOffset = 4 * 60;
      const adjustedDate = new Date(date.getTime() + timezoneOffset * 60000);
      const day = adjustedDate.getDate();
      const month = adjustedDate.getMonth() + 1;
      const year = adjustedDate.getFullYear();
      return `${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`;
    } else {
      return value;
    }
  }

  function handleYearFilterSelect(val: string) {
    setSelectedYear(val);
    filterData(val, selectedMonth, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handleMonthFilterSelect(val: string) {
    setSelectedMonth(val);
    filterData(selectedYear, val, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handlePackageFilterSelect(val: string) {
    filterData(selectedYear, selectedMonth, val, setToastVisibility, updatedData, setToastText, setNewData);
    setSelectedPackage(val);
  }

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const updatedDate = result.items[0] && returnDate(result.items[0].Date);
        const newItem = { ...result.items[0], Date: updatedDate };
        setNewData(prev => {
          const without = prev.filter((oItem: any) => oItem.ID !== item.ID);
          return [...without, newItem];
        });
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  function handleToastDismiss() {
    setToastVisibility(false);
  }

  const yearFilterOptions = useMemo(() => FilterChoices([...new Set(updatedData.map((item: any) => item.Year))], 'Year'), [updatedData]);
  const monthFilterOptions = useMemo(() => FilterChoices([...new Set(updatedData.map((item: any) => item.Month))], 'Month'), [updatedData]);
  const packageFilterOptions = useMemo(() => FilterChoices([...new Set(updatedData.map((item: any) => item.packageName))], 'Package'), [updatedData]);

  const chartData = useMemo(() => {
    const colorScale = scaleOrdinal(schemeCategory10);

    const uniquePeriod = [...new Set(dateRangeFiltered.map((item: any) => item.Period))].filter(Boolean) as string[];

    // Items with valid QuantityTonne — for line charts
    const qtyFiltered = dateRangeFiltered.filter((item: any) =>
      item.QuantityTonne != null && item.QuantityTonne !== ''
    );

    // --- Pie 1: SpoilType / QuantityTonne ---
    const pieAccum1: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.SpoilType == null) return;
      const key = String(item.SpoilType);
      pieAccum1[key] = (pieAccum1[key] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
    const bySpoilCategoryPieData = Object.entries(pieAccum1).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType5(index.toString()),
    }));

    // --- Pie 2: SpoilDisposalMethod / QuantityTonne ---
    const pieAccum2: Record<string, number> = {};
    dateRangeFiltered.forEach((item: any) => {
      if (item.SpoilDisposalMethod == null) return;
      const key = String(item.SpoilDisposalMethod);
      pieAccum2[key] = (pieAccum2[key] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const bySpoilDisposalMethodPieData = Object.entries(pieAccum2).map(([name, value], index) => ({
      name,
      value: Math.round(value),
      color: colorType3(index.toString()),
    }));

    // --- Line 1: SpoilType monthly QuantityTonne ---
    const uniqueSpoilTypes = [...new Set(qtyFiltered.map((item: any) => item.SpoilType))].filter(Boolean) as string[];
    const periodTypeSums: Record<string, Record<string, number>> = {};
    qtyFiltered.forEach((item: any) => {
      if (!item.Period || item.SpoilType == null) return;
      const p = String(item.Period);
      const t = String(item.SpoilType);
      if (!periodTypeSums[p]) periodTypeSums[p] = {};
      periodTypeSums[p][t] = (periodTypeSums[p][t] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineDataByType = {
      labels: uniquePeriod,
      datasets: uniqueSpoilTypes.map((type, index) => ({
        data: uniquePeriod.map(p => Math.round(periodTypeSums[p]?.[type] ?? 0)),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScale(String(index)),
      })),
      legend: uniqueSpoilTypes.map(type => ({ name: type })),
    };

    // --- Line 2: SpoilDisposalMethod monthly QuantityTonne ---
    const uniqueDisposalMethods = [...new Set(qtyFiltered.map((item: any) => item.SpoilDisposalMethod))].filter(Boolean) as string[];
    const periodMethodSums: Record<string, Record<string, number>> = {};
    qtyFiltered.forEach((item: any) => {
      if (!item.Period || item.SpoilDisposalMethod == null) return;
      const p = String(item.Period);
      const m = String(item.SpoilDisposalMethod);
      if (!periodMethodSums[p]) periodMethodSums[p] = {};
      periodMethodSums[p][m] = (periodMethodSums[p][m] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const lineDataByDisposal = {
      labels: uniquePeriod,
      datasets: uniqueDisposalMethods.map((method, index) => ({
        data: uniquePeriod.map(p => Math.round(periodMethodSums[p]?.[method] ?? 0)),
        strokeWidth: 2,
        color: (_opacity = 1) => colorScale(String(uniqueSpoilTypes.length + index)),
      })),
      legend: uniqueDisposalMethods.map(method => ({ name: method })),
    };

    // --- KPI ---
    const totalQty = dateRangeFiltered.reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0);
    const totalReused = dateRangeFiltered.filter((item: any) => item.Reused === 'Reuse').reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0);
    const totalNotReused = dateRangeFiltered.filter((item: any) => item.Reused === 'Not Reused').reduce((s: number, item: any) => s + (Number(item.QuantityTonne) || 0), 0);

    return {
      bySpoilCategoryPieData,
      bySpoilDisposalMethodPieData,
      lineDataByType,
      lineDataByDisposal,
      uniquePeriod,
      totalQty,
      totalReused,
      totalNotReused,
    };
  }, [dateRangeFiltered, spoilLogColumns]);

  const kpiItems = [
    { label: 'Total Qty (T)', value: Math.round(chartData.totalQty), color: '#00695C' },
    { label: 'Entries', value: dateRangeFiltered.length, color: '#4527A0' },
    { label: 'Reused (T)', value: Math.round(chartData.totalReused), color: '#2E7D32' },
    { label: 'Not Reused (T)', value: Math.round(chartData.totalNotReused), color: '#F57C00' },
  ];

  return (
    <View style={logStyles.logRoot}>
      <View style={CommonStyles.ribbonIconContainer}>
        <View style={CommonStyles.IconContainer}>
          <FilterIcon setFilterVisible={setFilterVisible} FilterVisible={FilterVisible} />
        </View>
        <View style={CommonStyles.IconContainer}>
          {EnableExport() === true
            ? <ExportIcon handleExportClick={handleExportClick} newData={newData} />
            : null
          }
        </View>
        <SelectedFilters selectedMonth={selectedMonth} selectedPackage={selectedPackage} selectedYear={selectedYear} />
      </View>
      <Modal
        animationType="fade"
        transparent={true}
        visible={FilterVisible}
        onRequestClose={() => { setFilterVisible(!FilterVisible); }}>
        <View style={formCustomStyles.centeredView}>
          <FilterBox
            FilterVisible={FilterVisible}
            setFilterVisible={setFilterVisible}
            screenWidth={screenWidth}
            CommonStyles={CommonStyles}
            yearFilterOptions={yearFilterOptions}
            handleYearFilterSelect={handleYearFilterSelect}
            selectedYear={selectedYear}
            monthFilterOptions={monthFilterOptions}
            handleMonthFilterSelect={handleMonthFilterSelect}
            selectedMonth={selectedMonth}
            packageFilterOptions={packageFilterOptions}
            handlePackageFilterSelect={handlePackageFilterSelect}
            selectedPackage={selectedPackage}
            handleFilterReset={handleFilterReset}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            setSelectedPackage={setSelectedPackage}
            setNewData={setNewData}
            updatedData={updatedData}
          />
        </View>
      </Modal>
      <View>
        <ToastMessage
          show={toastVisibility}
          actionLabel="Dismiss"
          toastMessage={toastText}
          duration={3000}
          onDismiss={handleToastDismiss}
        />
      </View>

      {isDashboard ? (
        <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
          <View style={{ zIndex: 200 }}>
            <LogKpiRow items={kpiItems}>
              <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
            </LogKpiRow>
          </View>
          <View>
            <View style={CommonStyles.container}>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                <PieChartComp data={chartData.bySpoilCategoryPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={"Spoil Qty/Type"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                <LineChartScreen legend={chartData.lineDataByType.legend} data={chartData.lineDataByType} label={chartData.lineDataByType.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={"Spoil Qty/Type"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <PieChartComp data={chartData.bySpoilDisposalMethodPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={"Spoil Qty/Destination"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <LineChartScreen legend={chartData.lineDataByDisposal.legend} data={chartData.lineDataByDisposal} label={chartData.lineDataByDisposal.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={"Spoil Qty/Destination"} />
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <TabsProvider defaultIndex={0}>
          <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
            <TabScreen label="Charts" icon="chart-bar">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View style={{ zIndex: 200 }}>
                  <LogKpiRow items={kpiItems}>
                    <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
                  </LogKpiRow>
                </View>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <PieChartComp data={chartData.bySpoilCategoryPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={"Spoil Qty/Type"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen legend={chartData.lineDataByType.legend} data={chartData.lineDataByType} label={chartData.lineDataByType.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={"Spoil Qty/Type"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <PieChartComp data={chartData.bySpoilDisposalMethodPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative - Tonne)'} ChartTitle={"Spoil Qty/Destination"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen legend={chartData.lineDataByDisposal.legend} data={chartData.lineDataByDisposal} label={chartData.lineDataByDisposal.labels} subTitle={'(Monthly - Tonne)'} ChartTitle={"Spoil Qty/Destination"} />
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
            <TabScreen label="List" icon="database">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={CommonStyles.cardThree}>
                      {newData && newData.length > 0 && ColumnsWithIDColumn && (
                        <PaperTableDynamic
                          data={newData}
                          columns={ColumnsWithIDColumn}
                          siteId={siteId}
                          logTitle={logTitle}
                          updateData={handleDataUpdate}
                          isConsolidatedTable={true}
                        />
                      )}
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
          </Tabs>
        </TabsProvider>
      )}
    </View>
  );
};
